import React, { useMemo, useState } from 'react';
import { useStylesGeneral } from '../../../styles/StyleComponents';
import "../../../styles/site.css";
import { Button, CircularProgress, TextField, Snackbar } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Alert } from '@material-ui/lab';
import AccordionCS from '../../../common/ComponentesCS/AccordionCS';
import MaterialTableCS from '../../../common/ComponentesCS/MaterialTableCS';
import ModalCS from '../../../common/ComponentesCS/ModalCS';
import { CatalogsService, invoicesService, ProjectsService } from '../../../common/services';

const tableActionButtonStyle = {
    backgroundColor: '#1976d2',
    color: 'white',
    padding: '5px 10px',
    fontSize: '12px',
    textTransform: 'none'
};

const tableDangerActionButtonStyle = {
    ...tableActionButtonStyle,
    backgroundColor: '#d32f2f'
};

const tableActionButtonDisabledStyle = {
    ...tableActionButtonStyle,
    backgroundColor: '#bdbdbd'
};

const tableCenteredColumnStyle = {
    textAlign: 'center'
};

const tableCenteredContentStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    gap: '4px'
};

const ProjectDetails = (props) => {
    const {
        projectData,
        clientInvoices,
        projectInvoices,
        invoicesSelected,
        setInvoicesSelected,
        productsSelected,
        setProductsSelected,
        productSeries,
        setProductSeries,
        availableInvoicesCatalog = [],
        availableInvoicesLoading = false,
        availableInvoicesError = null,
        reloadProjectData
    } = props;

    const classes = useStylesGeneral();
    const [selectedInvoiceOption, setSelectedInvoiceOption] = useState(null);
    const [mutatingInvoiceId, setMutatingInvoiceId] = useState(null);
    const [isAddingInvoice, setIsAddingInvoice] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [invoiceToRemove, setInvoiceToRemove] = useState(null);
    const [showChangeProductModal, setShowChangeProductModal] = useState(false);
    const [productToChange, setProductToChange] = useState(null);
    const [articleOptions, setArticleOptions] = useState([]);
    const [selectedArticleOption, setSelectedArticleOption] = useState(null);
    const [isLoadingArticles, setIsLoadingArticles] = useState(false);
    const [isUpdatingArticle, setIsUpdatingArticle] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const availableInvoiceOptions = useMemo(() => {
        return availableInvoicesCatalog
            .map((catalogItem) => catalogItem.autocompleteOption || catalogItem)
            .filter((option) => {
                const currentIdFactura = option?.idFactura;
                const currentFactura = option?.factura;

                const existsInClientInvoices = clientInvoices.some((invoice) =>
                    (currentIdFactura !== undefined && currentIdFactura !== null && invoice.idFactura === currentIdFactura) ||
                    (currentFactura && invoice.factura === currentFactura)
                );

                const existsInProjectInvoices = projectInvoices.some((invoiceDetail) =>
                    (currentIdFactura !== undefined && currentIdFactura !== null && invoiceDetail.idFactura === currentIdFactura) ||
                    (currentFactura && invoiceDetail.factura === currentFactura)
                );

                return !existsInClientInvoices && !existsInProjectInvoices;
            });
    }, [availableInvoicesCatalog, clientInvoices, projectInvoices]);

    const selectableInvoices = useMemo(() => (
        clientInvoices.filter((invoice) => Boolean(invoice?.idFactura))
    ), [clientInvoices]);

    const selectableInvoiceIds = useMemo(() => (
        selectableInvoices.map((invoice) => invoice.idFactura)
    ), [selectableInvoices]);

    const getInvoiceObject = (idFactura) => clientInvoices.find((invoice) => invoice.idFactura === idFactura);

    const getInvoiceDetail = (idFactura) => projectInvoices.find((invoice) => invoice.idFactura === idFactura);

    const buildSelectedProductsForInvoice = (idFactura, currentSelections = []) => {
        const availableProducts = getInvoiceDetail(idFactura)?.articleDetails || [];

        return availableProducts.map((product) => (
            currentSelections.find((selectedProduct) => selectedProduct.idArticulo === product.idArticulo) || product
        ));
    };

    const clearProductSeriesForInvoices = (invoiceIds = []) => {
        if (!setProductSeries || invoiceIds.length === 0) {
            return;
        }

        setProductSeries((prevState) => {
            const newState = { ...prevState };

            Object.keys(newState).forEach((key) => {
                if (invoiceIds.some((invoiceId) => key.startsWith(`${invoiceId}-`))) {
                    delete newState[key];
                }
            });

            return newState;
        });
    };

    const handleInvoiceSelectionChange = (idFactura, shouldSelect) => {
        const invoiceObject = getInvoiceObject(idFactura);
        if (!invoiceObject) {
            return;
        }

        setInvoicesSelected((prevInvoices) => {
            if (shouldSelect) {
                if (prevInvoices.some((invoice) => invoice.idFactura === idFactura)) {
                    return prevInvoices;
                }

                return [...prevInvoices, invoiceObject];
            }

            return prevInvoices.filter((invoice) => invoice.idFactura !== idFactura);
        });

        setProductsSelected((prevState) => {
            const newState = { ...prevState };

            if (shouldSelect) {
                const nextSelections = buildSelectedProductsForInvoice(idFactura, prevState[idFactura] || []);

                if (nextSelections.length > 0) {
                    newState[idFactura] = nextSelections;
                }
            } else {
                delete newState[idFactura];
            }

            return newState;
        });

        if (!shouldSelect) {
            clearProductSeriesForInvoices([idFactura]);
        }
    };

    const handleToggleAllInvoices = () => {
        const selectedInvoiceIds = invoicesSelected.map((invoice) => invoice.idFactura);
        const allInvoicesSelected = selectableInvoiceIds.length > 0
            && selectableInvoiceIds.every((idFactura) => selectedInvoiceIds.includes(idFactura));

        if (allInvoicesSelected) {
            setInvoicesSelected([]);
            setProductsSelected({});
            clearProductSeriesForInvoices(selectableInvoiceIds);

            return;
        }

        setInvoicesSelected(selectableInvoices);
        setProductsSelected((prevState) => {
            const newState = { ...prevState };

            selectableInvoiceIds.forEach((idFactura) => {
                const nextSelections = buildSelectedProductsForInvoice(idFactura, prevState[idFactura] || []);

                if (nextSelections.length > 0) {
                    newState[idFactura] = nextSelections;
                }
            });

            return newState;
        });
    };

    const handleProductCheckboxChange = (idFactura, idArticulo) => {
        const invoiceDetail = projectInvoices.find((inv) => inv.idFactura === idFactura);
        const productObject = invoiceDetail?.articleDetails.find((product) => product.idArticulo === idArticulo);

        if (!productObject) return;

        setProductsSelected((prevState) => {
            const currentFacturaSelections = prevState[idFactura] || [];
            const isCurrentlySelected = currentFacturaSelections.some((product) => product.idArticulo === idArticulo);
            const newFacturaSelections = isCurrentlySelected
                ? currentFacturaSelections.filter((product) => product.idArticulo !== idArticulo)
                : [...currentFacturaSelections, productObject];

            const newState = {
                ...prevState,
                [idFactura]: newFacturaSelections
            };

            setInvoicesSelected((prevCheckboxes) => {
                const invoiceObject = clientInvoices.find((invoice) => invoice.idFactura === idFactura);
                if (!invoiceObject) return prevCheckboxes;

                let newCheckboxes = [...prevCheckboxes];

                if (!isCurrentlySelected) {
                    if (!newCheckboxes.some((invoice) => invoice.idFactura === idFactura)) {
                        newCheckboxes.push(invoiceObject);
                    }
                } else if (newFacturaSelections.length === 0) {
                    newCheckboxes = newCheckboxes.filter((invoice) => invoice.idFactura !== idFactura);
                }

                return newCheckboxes;
            });

            return newState;
        });
    };

    const handleToggleAllProducts = (idFactura) => {
        const invoiceDetail = projectInvoices.find((inv) => inv.idFactura === idFactura);
        const availableProducts = invoiceDetail?.articleDetails || [];

        if (!invoiceDetail || availableProducts.length === 0) {
            return;
        }

        const currentSelections = productsSelected[idFactura] || [];
        const currentSelectionIds = currentSelections.map((product) => product.idArticulo);
        const allProductsSelected = availableProducts.every((product) => currentSelectionIds.includes(product.idArticulo));

        handleInvoiceSelectionChange(idFactura, !allProductsSelected);
    };

    const cleanupInvoiceState = (idFactura) => {
        setInvoicesSelected((prevState) => prevState.filter((invoice) => invoice.idFactura !== idFactura));

        setProductsSelected((prevState) => {
            const newState = { ...prevState };
            delete newState[idFactura];
            return newState;
        });

        if (setProductSeries) {
            setProductSeries((prevState) => {
                const newState = { ...prevState };

                Object.keys(newState).forEach((key) => {
                    if (key.startsWith(`${idFactura}-`)) {
                        delete newState[key];
                    }
                });

                return newState;
            });
        }
    };

    const showToast = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setSnackbar({ open: false, message: '', severity: 'success' });
    };

    const handleOpenRemoveModal = (rowData) => {
        if (!rowData?.idFactura) {
            showToast('No se pudo identificar la factura a remover.', 'error');
            return;
        }

        setInvoiceToRemove(rowData);
        setShowRemoveModal(true);
    };

    const handleCloseRemoveModal = (forceClose = false) => {
        if (mutatingInvoiceId && !forceClose) {
            return;
        }

        setShowRemoveModal(false);
        setInvoiceToRemove(null);
    };

    const handleOpenChangeProductModal = async (rowData) => {
        if (!rowData?.idArticulo) {
            showToast('No se pudo identificar el articulo a cambiar.', 'error');
            return;
        }

        setProductToChange(rowData);
        setSelectedArticleOption(null);
        setArticleOptions([]);
        setShowChangeProductModal(true);

        try {
            setIsLoadingArticles(true);
            const response = await CatalogsService.obtenerArticulos();

            if (response?.success === false) {
                throw new Error(response?.message || 'No se pudo cargar el catalogo de articulos.');
            }

            const options = Array.isArray(response?.data)
                ? response.data
                    .filter((article) => article?.articulo)
                    .map((article) => ({
                        ...article,
                        label: article.articulo
                    }))
                : [];

            setArticleOptions(options);
        } catch (error) {
            showToast(error?.message || 'No se pudo cargar el catalogo de articulos.', 'error');
        } finally {
            setIsLoadingArticles(false);
        }
    };

    const handleCloseChangeProductModal = (forceClose = false) => {
        if (isUpdatingArticle && !forceClose) {
            return;
        }

        setShowChangeProductModal(false);
        setProductToChange(null);
        setSelectedArticleOption(null);
        setArticleOptions([]);
    };

    const handleChangeProduct = async () => {
        if (!productToChange?.idArticulo || !selectedArticleOption?.articulo) {
            showToast('Selecciona un articulo antes de actualizarlo.', 'warning');
            return;
        }

        if (selectedArticleOption.articulo === productToChange.articulo) {
            showToast('Selecciona un articulo diferente al actual.', 'warning');
            return;
        }

        try {
            setIsUpdatingArticle(true);
            const response = await ProjectsService.actualizarArticuloInterno({
                IdArticulo: productToChange.idArticulo,
                Articulo: selectedArticleOption.articulo,
                Descripcion1: selectedArticleOption.descripcion1
            });

            if (response?.success === false || response?.success === 0) {
                throw new Error(response?.message || 'No se pudo actualizar el articulo.');
            }

            setProductsSelected((prevState) => {
                const selectedProducts = prevState[productToChange.idFactura] || [];
                const isSelected = selectedProducts.some((product) => product.idArticulo === productToChange.idArticulo);

                if (!isSelected) {
                    return prevState;
                }

                return {
                    ...prevState,
                    [productToChange.idFactura]: selectedProducts.map((product) => (
                        product.idArticulo === productToChange.idArticulo
                            ? {
                                ...product,
                                articulo: selectedArticleOption.articulo,
                                descripcion: selectedArticleOption.descripcion1,
                                descripcion1: selectedArticleOption.descripcion1
                            }
                            : product
                    ))
                };
            });

            await reloadProjectData?.();
            showToast(response?.message || 'Articulo actualizado correctamente.', 'success');
            handleCloseChangeProductModal(true);
        } catch (error) {
            showToast(error?.message || 'No se pudo actualizar el articulo.', 'error');
        } finally {
            setIsUpdatingArticle(false);
        }
    };

    const handleAddInvoice = async () => {
        if (!selectedInvoiceOption?.invoice || !selectedInvoiceOption?.invoiceDetail) {
            showToast('Selecciona una factura antes de agregarla.', 'warning');
            return;
        }

        if (!projectData?.idProyectoD) {
            showToast('No se encontro el identificador del proyecto.', 'error');
            return;
        }

        const selectedInvoice = selectedInvoiceOption.invoice;
        const selectedInvoiceDetail = selectedInvoiceOption.invoiceDetail;

        const alreadyExists = clientInvoices.some((invoice) =>
            (selectedInvoice.idFactura !== undefined && selectedInvoice.idFactura !== null && invoice.idFactura === selectedInvoice.idFactura) ||
            (selectedInvoice.factura && invoice.factura === selectedInvoice.factura)
        ) || projectInvoices.some((invoiceDetail) =>
            (selectedInvoiceDetail.idFactura !== undefined && selectedInvoiceDetail.idFactura !== null && invoiceDetail.idFactura === selectedInvoiceDetail.idFactura) ||
            (selectedInvoiceDetail.factura && invoiceDetail.factura === selectedInvoiceDetail.factura)
        );

        if (alreadyExists) {
            showToast('La factura seleccionada ya existe en este wizard.', 'warning');
            return;
        }

        try {
            setIsAddingInvoice(true);
            await invoicesService.changeProjectInvoice(projectData.idProyectoD, selectedInvoice.idFactura);
            await reloadProjectData?.();
            setSelectedInvoiceOption(null);
            showToast('Factura agregada correctamente al proyecto.', 'success');
        } catch (error) {
            showToast(error?.message || 'No se pudo agregar la factura al proyecto.', 'error');
        } finally {
            setIsAddingInvoice(false);
        }
    };

    const handleRemoveInvoice = async () => {
        if (!invoiceToRemove?.idFactura) {
            showToast('No se pudo identificar la factura a remover.', 'error');
            return;
        }

        try {
            setMutatingInvoiceId(invoiceToRemove.idFactura);
            await ProjectsService.removeInvoice(invoiceToRemove.idFactura);
            cleanupInvoiceState(invoiceToRemove.idFactura);
            await reloadProjectData?.();
            showToast('Factura removida correctamente del proyecto.', 'success');
            handleCloseRemoveModal(true);
        } catch (error) {
            showToast(error?.message || 'No se pudo remover la factura del proyecto.', 'error');
        } finally {
            setMutatingInvoiceId(null);
        }
    };

    const getAllSelectedProducts = () => {
        const allSelected = [];
        Object.entries(productsSelected).forEach(([idFactura, products]) => {
            products.forEach((product) => {
                allSelected.push({
                    idFactura,
                    idArticulo: product.idArticulo,
                    product
                });
            });
        });
        return allSelected;
    };

    const isProductSelected = (idFactura, idArticulo) => {
        return productsSelected[idFactura]?.some((product) => product.idArticulo === idArticulo) || false;
    };

    const getInvoiceSelectionHeaderCheckbox = () => {
        const selectedInvoiceIds = invoicesSelected.map((invoice) => invoice.idFactura);
        const selectedCount = selectableInvoiceIds.filter((idFactura) => selectedInvoiceIds.includes(idFactura)).length;
        const allSelected = selectableInvoiceIds.length > 0 && selectedCount === selectableInvoiceIds.length;
        const indeterminate = selectedCount > 0 && !allSelected;

        return (
            <div style={tableCenteredContentStyle}>
                <Checkbox
                    color="primary"
                    checked={allSelected}
                    indeterminate={indeterminate}
                    onChange={handleToggleAllInvoices}
                    onClick={(event) => event.stopPropagation()}
                    disabled={selectableInvoiceIds.length === 0 || Boolean(mutatingInvoiceId)}
                />
            </div>
        );
    };

    const getProductSelectionHeaderCheckbox = (idFactura) => {
        const invoiceDetail = projectInvoices.find((invoice) => invoice.idFactura === idFactura);
        const availableProducts = invoiceDetail?.articleDetails || [];
        const selectedProductsForInvoice = productsSelected[idFactura] || [];
        const selectedCount = availableProducts.filter((product) =>
            selectedProductsForInvoice.some((selectedProduct) => selectedProduct.idArticulo === product.idArticulo)
        ).length;
        const allSelected = availableProducts.length > 0 && selectedCount === availableProducts.length;
        const indeterminate = selectedCount > 0 && !allSelected;

        return (
            <div style={tableCenteredContentStyle}>
                <Checkbox
                    color="primary"
                    checked={allSelected}
                    indeterminate={indeterminate}
                    onChange={() => handleToggleAllProducts(idFactura)}
                    onClick={(event) => event.stopPropagation()}
                    disabled={availableProducts.length === 0}
                />
            </div>
        );
    };

    const getProductColumns = (idFactura) => [
        {
            field: 'factura',
            title: 'Factura',
        },
        {
            field: 'articulo',
            title: 'Articulo',
        },
        {
            field: 'cantidad',
            title: 'Cantidad',
            render: (rowData) => rowData?.cantidadPendiente ? rowData?.cantidadPendiente : rowData?.cantidad
        },
        {
            field: 'descripcion',
            title: 'Descripcion',
        },
        {
            field: 'Seleccionar',
            title: getProductSelectionHeaderCheckbox(idFactura),
            sorting: false,
            headerStyle: tableCenteredColumnStyle,
            cellStyle: tableCenteredColumnStyle,
            render: (rowData) => (
                <Checkbox
                    name={`${rowData.idFactura}-${rowData.idArticulo}`}
                    color="primary"
                    checked={isProductSelected(rowData.idFactura, rowData.idArticulo)}
                    onChange={() => handleProductCheckboxChange(rowData.idFactura, rowData.idArticulo)}
                />
            )
        },
        {
            field: 'Cambiar producto',
            title: 'Cambiar producto',
            sorting: false,
            headerStyle: tableCenteredColumnStyle,
            cellStyle: tableCenteredColumnStyle,
            render: (rowData) => {
                const isDisabled = !rowData?.idArticulo || isLoadingArticles || isUpdatingArticle;

                return (
                    <div style={tableCenteredContentStyle}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={isDisabled}
                            onClick={() => handleOpenChangeProductModal(rowData)}
                            style={isDisabled ? tableActionButtonDisabledStyle : tableActionButtonStyle}
                        >
                            Cambiar producto
                        </Button>
                    </div>
                );
            }
        },
    ];

    const columns = [
        {
            field: 'factura',
            title: 'Factura',
            render: (rowData) => `${rowData.idFactura} - ${rowData.factura}`
        },
        {
            field: 'fechaActivo',
            title: 'Fecha Activo',
        },
        {
            field: 'Seleccionar',
            title: getInvoiceSelectionHeaderCheckbox(),
            sorting: false,
            headerStyle: tableCenteredColumnStyle,
            cellStyle: tableCenteredColumnStyle,
            render: (rowData) => (
                <Checkbox
                    name={rowData.idFactura}
                    color="primary"
                    checked={invoicesSelected.some((invoice) => invoice.idFactura === rowData.idFactura)}
                    onChange={() => handleInvoiceSelectionChange(
                        rowData.idFactura,
                        !invoicesSelected.some((invoice) => invoice.idFactura === rowData.idFactura)
                    )}
                    disabled={mutatingInvoiceId === rowData.idFactura}
                />
            )
        },
        {
            field: 'Remover',
            title: 'Remover',
            sorting: false,
            headerStyle: tableCenteredColumnStyle,
            cellStyle: tableCenteredColumnStyle,
            render: (rowData) => {
                const isDisabled = !rowData?.idFactura || mutatingInvoiceId === rowData.idFactura || isAddingInvoice;

                return (
                    <div style={tableCenteredContentStyle}>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            disabled={isDisabled}
                            onClick={() => handleOpenRemoveModal(rowData)}
                            style={isDisabled ? tableActionButtonDisabledStyle : tableDangerActionButtonStyle}
                        >
                            Remover
                        </Button>
                    </div>
                );
            }
        }
    ];

    return (
        <div className={`${classes.root}`} style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '20px' }}>
            {/* <div style={{ display: 'flex', flexDirection: 'row', padding: '10px', gap: '15px', alignSelf: 'flex-end' }}>
                <Button
                    className="botonAzul floatLeft"
                    onClick={() => {
                        const allSelected = getAllSelectedProducts();
                    }}
                    variant="contained"
                >
                    Mostrar Seleccionados
                </Button>
            </div> */}

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', maxWidth: '480px' }}>
                <div style={{ minWidth: '320px', maxWidth: '320px', flex: '1 1 320px' }}>
                    <Autocomplete
                        options={availableInvoiceOptions}
                        loading={availableInvoicesLoading}
                        value={selectedInvoiceOption}
                        onChange={(event, newValue) => setSelectedInvoiceOption(newValue)}
                        getOptionLabel={(option) => option?.idFactura && option?.factura ? `${option.idFactura} - ${option.factura}` : option?.label || ''}
                        getOptionSelected={(option, value) => option?.id === value?.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Factura disponible"
                                variant="outlined"
                                placeholder="Selecciona una factura"
                                error={Boolean(availableInvoicesError)}
                                helperText={availableInvoicesError ? 'No se pudo cargar el catalogo de facturas.' : ''}
                            />
                        )}
                    />
                </div>
                <Button
                    className="botonAzul"
                    onClick={handleAddInvoice}
                    variant="contained"
                    disabled={availableInvoicesLoading || availableInvoiceOptions.length === 0 || isAddingInvoice}
                    style={{ height: '56px' }}
                >
                    {isAddingInvoice ? 'Agregando...' : 'Agregar factura'}
                </Button>
            </div>

            <div style={{ marginBottom: '15px' }}>
                <MaterialTableCS
                    data={clientInvoices}
                    columns={columns}
                    title=""
                    pageSize={5}
                    showTitle={false}
                    selection={false}
                    actions={[]}
                    pageSizeOptions={[5, 10, 25, 50]}
                    toolbar={false}
                    search={true}
                    paging={true}
                    autoHeight={true}
                />
            </div>
            {projectInvoices.map((invoiceDetail, index) => (
                <AccordionCS
                    key={`accordion-${invoiceDetail.idFactura || index}`}
                    title={`Factura: ${invoiceDetail.factura || `Sin Factura ${index + 1}`}`}
                    descripcion={`Articulos de la factura ${invoiceDetail.factura || `Sin Factura ${index + 1}`}`}
                    defaultExpanded={index === 0}
                >
                    <MaterialTableCS
                        data={invoiceDetail.articleDetails}
                        columns={getProductColumns(invoiceDetail.idFactura)}
                        title=""
                        pageSize={10}
                        showTitle={false}
                        selection={false}
                        actions={[]}
                        pageSizeOptions={[5, 10, 25, 50]}
                        toolbar={false}
                        search={true}
                        paging={true}
                        autoHeight={true}
                    />
                </AccordionCS>
            ))}

            <ModalCS
                show={showRemoveModal}
                title="Confirmar remocion de factura"
                size="md"
                handleClose={handleCloseRemoveModal}
            >
                <div style={{ padding: '10px 5px' }}>
                    <p style={{ marginBottom: '20px' }}>
                        {`Se removera la factura ${invoiceToRemove?.factura || invoiceToRemove?.idFactura || ''} del proyecto. Deseas continuar?`}
                    </p>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <Button
                            variant="outlined"
                            onClick={handleCloseRemoveModal}
                            disabled={Boolean(mutatingInvoiceId)}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleRemoveInvoice}
                            disabled={Boolean(mutatingInvoiceId)}
                        >
                            {mutatingInvoiceId ? 'Removiendo...' : 'Confirmar'}
                        </Button>
                    </div>
                </div>
            </ModalCS>

            <ModalCS
                show={showChangeProductModal}
                title="Cambiar producto seleccionado"
                size="md"
                handleClose={handleCloseChangeProductModal}
            >
                <div style={{ padding: '10px 5px' }}>
                    <div style={{ marginBottom: '20px' }}>
                        <strong>Articulo seleccionado</strong>
                        <p style={{ margin: '6px 0 0' }}>
                            {`${productToChange?.articulo || ''}${productToChange?.descripcion || productToChange?.descripcion1 ? ` - ${productToChange?.descripcion || productToChange?.descripcion1}` : ''}`}
                        </p>
                    </div>

                    <Autocomplete
                        options={articleOptions}
                        loading={isLoadingArticles}
                        value={selectedArticleOption}
                        onChange={(event, newValue) => setSelectedArticleOption(newValue)}
                        getOptionLabel={(option) => option?.articulo || ''}
                        getOptionSelected={(option, value) => option?.articulo === value?.articulo}
                        renderOption={(option) => (
                            <div>
                                <strong>{option.articulo}</strong>
                                <div>{option.descripcion1 || 'Sin descripcion'}</div>
                            </div>
                        )}
                        noOptionsText={isLoadingArticles ? 'Cargando articulos...' : 'No se encontraron articulos'}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Buscar articulo"
                                variant="outlined"
                                fullWidth
                                placeholder="Escribe el codigo del articulo"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isLoadingArticles ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    )
                                }}
                            />
                        )}
                    />

                    {selectedArticleOption && (
                        <div style={{ marginTop: '20px' }}>
                            <strong>Informacion del articulo nuevo</strong>
                            <p style={{ margin: '6px 0 0' }}>
                                {`${selectedArticleOption.articulo} - ${selectedArticleOption.descripcion1 || 'Sin descripcion'}`}
                            </p>
                        </div>
                    )}

                    {selectedArticleOption && (
                        <p style={{ marginTop: '20px', marginBottom: '20px' }}>
                            {`Seguro que quieres cambiar articulo ${productToChange?.articulo || ''} por articulo ${selectedArticleOption.articulo}?`}
                        </p>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '25px' }}>
                        <Button
                            variant="outlined"
                            onClick={handleCloseChangeProductModal}
                            disabled={isUpdatingArticle}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleChangeProduct}
                            disabled={isLoadingArticles || isUpdatingArticle || !selectedArticleOption || selectedArticleOption.articulo === productToChange?.articulo}
                        >
                            {isUpdatingArticle ? 'Actualizando...' : 'Aceptar'}
                        </Button>
                    </div>
                </div>
            </ModalCS>

            <Snackbar
                className="app-toast"
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default ProjectDetails;
